import React from 'react';
import { useAppContext } from 'src/components/app-context';
import { PropertyValue as PropertyValueBase } from './components/property-value';
import { Property } from './components/types';
import {
  PropertyInterface,
  PropertyBoolean,
  PropertyString,
  PropertyNumber,
  PropertyInterval,
  PropertyList,
} from '../../types';

/**
 * The automatic types we get from our GraphQL schema isn't compatible with TS discriminated
 * unions, so we do some wrangling here to make it more usable. Should still be fairly typesafe.
 */
function sanitizeProperty(property: PropertyInterface): Property | null {
  const { type } = property;
  switch (type) {
    case 'boolean': {
      const { boolean = null } = property as PropertyBoolean;
      return { type, boolean };
    }
    case 'string': {
      const { value = null, unit = null } = property as PropertyString;
      return { type, value, unit };
    }
    case 'integer':
    case 'decimal': {
      const { number = null, unit = null } = property as PropertyNumber;
      return { type, number, unit };
    }
    case 'interval': {
      const {
        from = null,
        to = null,
        unit = null,
      } = property as PropertyInterval;
      return { type, from, to, unit };
    }
    case 'list': {
      const { values = null } = property as PropertyList;
      return { type, values };
    }
    default:
      return null;
  }
}

type Props = {
  property: PropertyInterface;
};

export function PropertyValue({ property }: Props) {
  const { t } = useAppContext();
  const sanitizedProperty = sanitizeProperty(property);
  if (!sanitizedProperty) {
    return null;
  }

  return (
    <PropertyValueBase
      property={sanitizedProperty}
      booleanTrueText={t('category', 'property.boolean-true')}
      booleanFalseText={t('category', 'property.boolean-false')}
    />
  );
}
